import type { JSX } from "react";
import { Link } from "react-router-dom";
import type { PortfolioMover, PortfolioMovers } from "@finance-os/shared-types/api";

import { formatDay, usePortfolioFormat } from "../../lib/use-portfolio-format.js";
import { cn } from "../../lib/utils.js";
import { Badge } from "../ui/badge.js";
import { Pl } from "./Pl.js";
import { PanelCard, type PanelQuery } from "./PanelCard.js";

function MoverList({ title, items }: { title: string; items: PortfolioMover[] }): JSX.Element {
  const fmt = usePortfolioFormat();
  return (
    <div className="min-w-0">
      <div className="text-muted-foreground mb-1 text-xs font-medium tracking-wide uppercase">{title}</div>
      {items.length === 0 ? (
        <p className="text-muted-foreground text-sm">—</p>
      ) : (
        <ul className="divide-border divide-y">
          {items.map((m) => (
            <li key={m.security_id} className="flex items-center gap-3 py-2">
              <Link to={`/stocks/s/${m.security_id}`} className="text-brand min-w-0 flex-1 hover:underline">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{m.symbol}</span>
                  {m.basis === "lifetime" && <Badge variant="muted">since purchase</Badge>}
                </div>
                {m.name && <div className="text-muted-foreground truncate text-xs">{m.name}</div>}
              </Link>
              <div className="shrink-0 text-right text-sm tabular-nums">
                <div className={cn("font-medium", m.return_pct < 0 ? "text-bad" : "text-good")}>
                  {fmt.pct(m.return_pct, { signed: true })}
                </div>
                <div className="text-xs">
                  <Pl minor={m.pl_eur_minor} />
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Best and worst holdings by return over the window. */
export function MoversPanel({ query }: { query: PanelQuery<PortfolioMovers> }): JSX.Element {
  const data = query.data;
  const none = data?.best.length === 0 && data.worst.length === 0;
  return (
    <PanelCard
      title="Top movers"
      isLoading={query.isLoading}
      isError={query.isError}
      empty={none ? "No open positions to rank yet." : null}
    >
      {data && (
        <>
          <div className="grid gap-4">
            <MoverList title="Best" items={data.best} />
            <MoverList title="Worst" items={data.worst} />
          </div>
          <p className="text-muted-foreground text-xs">
            Return since {formatDay(data.window.from)}. “Since purchase” marks holdings with no history that far back.
          </p>
        </>
      )}
    </PanelCard>
  );
}
